import React, { useMemo } from 'react';
import { Info } from 'lucide-react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { formatMacro } from '@shared/utils/formatting';
import { PILLARS, ALL_PILLAR_KEYS, NUTRIENT_REGISTRY, normalizeNutrientKey } from '@/shared/constants/metabolic-pillars';

interface BioLoadPreviewProps {
    data: any; // Accept totals object
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
    title?: string;
    accentColor?: string;
}

/**
 * BioLoadPreview: Shared component for showing nutrient distribution across pillars.
 * V8.4: FIXED. Correctly normalizes keys to prevent duplication in 'Other'.
 */
export const BioLoadPreview: React.FC<BioLoadPreviewProps> = ({ 
    data, isExpanded, setIsExpanded, title = "SESSION BIO-LOAD", accentColor = "#a855f7" 
}) => {
    
    const totals = data || {};
    const micros = totals.micros || totals; 

    const resolveValue = (key: string) => {
        const val = micros[key];
        if (!val) return 0;
        if (typeof val === 'object' && 'total' in val) return val.total;
        if (typeof val === 'object' && 'amount' in val) return val.amount;
        return Number(val) || 0;
    };

    const resolveUnit = (key: string) => {
        const val = micros[key];
        if (val && typeof val === 'object' && val.unit) return val.unit;
        const lowKey = normalizeNutrientKey(key);
        return NUTRIENT_REGISTRY[lowKey]?.unit || 'mg';
    };

    const hasPillarData = PILLARS.some(p => p.keys.some(k => resolveValue(k) > 0));
    
    // Identify non-pillar nutrients (Hardened normalization)
    const otherKeys = useMemo(() => {
        const blacklist = [
            ...ALL_PILLAR_KEYS, 'discovered_micros', 'detailed_metrics', 'id', 'item_name', 'quantity', 
            'calories', 'protein', 'carbs', 'fat', 'insulin_load', 'glycemic_index', 'food_insulin_index',
            'dosage', 'compound_name', 'timing_window', 'items_checked'
        ];
        return Object.keys(micros).filter(k => {
            const canonicalKey = normalizeNutrientKey(k);
            return !blacklist.includes(canonicalKey) && resolveValue(k) > 0;
        });
    }, [micros]);

    const hasAnyData = hasPillarData || otherKeys.length > 0;

    return (
        <Box style={{ marginTop: 30, padding: 20, backgroundColor: `${accentColor}0d`, borderRadius: 16, borderWidth: 1, borderColor: `${accentColor}33` }}>
            <Pressable 
                onPress={() => setIsExpanded(!isExpanded)}
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: isExpanded && hasAnyData ? 20 : 0 }}
            >
                <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <Info size={18} color={accentColor} />
                    <Typography style={{ fontSize: 12, fontWeight: '950', color: '#fff', letterSpacing: 1.5 }}>{title}</Typography>
                </Box>
                <Typography style={{ fontSize: 11, color: accentColor, fontWeight: '900', letterSpacing: 1 }}>{isExpanded ? 'MINIMIZE' : 'EXPAND'}</Typography>
            </Pressable>
            
            {isExpanded && !hasAnyData && (
                <Typography style={{ fontSize: 10, color: '#444', marginTop: 15, fontStyle: 'italic', textAlign: 'center' }}>No active compounds detected in this log.</Typography>
            )}

            {isExpanded && hasAnyData && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
                    {/* 1. CANONICAL PILLARS */}
                    {PILLARS.map(pillar => {
                        const activeInPillar = pillar.keys.filter(k => resolveValue(k) > 0);
                        if (activeInPillar.length === 0) return null;

                        return (
                            <Box key={pillar.id}>
                                <Typography style={{ fontSize: 11, fontWeight: '950', color: pillar.color, marginBottom: 10, letterSpacing: 1.5 }}>{pillar.label}</Typography>
                                <Box style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 15 }}>
                                    {activeInPillar.map(key => {
                                        const profile = NUTRIENT_REGISTRY[normalizeNutrientKey(key)];
                                        const label = profile?.label || key.replace(/_/g, ' ');
                                        const subLabel = profile?.subLabel;
                                        return (
                                            <Box key={key} style={{ alignItems: 'flex-start', minWidth: '28%' }}>
                                                <Typography style={{ fontSize: 18, fontWeight: '950', color: pillar.color, fontVariantNumeric: 'lining-nums' } as any}>
                                                    {formatMacro(resolveValue(key))}
                                                    <Typography style={{ fontSize: 10, color: '#555', fontWeight: '900' }}> {resolveUnit(key)}</Typography>
                                                </Typography>
                                                <Typography style={{ fontSize: 9, fontWeight: '900', color: '#777', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                                                {subLabel && (
                                                    <Typography style={{ fontSize: 7, color: '#444', fontWeight: '800', textTransform: 'uppercase', marginTop: 1 }}>{subLabel}</Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}

                    {/* 2. OTHER ACTIVE COMPOUNDS (Unified Section) */}
                    {otherKeys.length > 0 && (
                        <Box style={{ borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)', paddingTop: 15 }}>
                            <Typography style={{ fontSize: 11, fontWeight: '950', color: '#06b6d4', marginBottom: 10, letterSpacing: 1.5 }}>OTHER ACTIVE COMPOUNDS</Typography>
                            <Box style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 15 }}>
                                {otherKeys.map(key => {
                                    const profile = NUTRIENT_REGISTRY[normalizeNutrientKey(key)];
                                    const label = profile?.label || key.replace(/_/g, ' ');
                                    const subLabel = profile?.subLabel;
                                    return (
                                        <Box key={key} style={{ alignItems: 'flex-start', minWidth: '28%' }}>
                                            <Typography style={{ fontSize: 18, fontWeight: '950', color: '#06b6d4', fontVariantNumeric: 'lining-nums' } as any}>
                                                {formatMacro(resolveValue(key))}
                                                <Typography style={{ fontSize: 10, color: '#555', fontWeight: '900' }}> {resolveUnit(key)}</Typography>
                                            </Typography>
                                            <Typography style={{ fontSize: 9, fontWeight: '900', color: '#777', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                                            {subLabel && (
                                                <Typography style={{ fontSize: 7, color: '#444', fontWeight: '800', textTransform: 'uppercase', marginTop: 1 }}>{subLabel}</Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};
